/*!
 * Oneskin Product Item — vanilla JS controller for quick-add / quick-view triggers
 * on product cards. Used site-wide: collection grids, search, recommended products,
 * featured collection grid/slider, masonry products, complete-the-look, shoppable,
 * recently-viewed, and the PDP's own featured_products block.
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'product-item.js' | asset_url }}" defer="defer"></script>
 *
 * Replaces frontend/scripts/lib/product-item.js + frontend/scripts/lib/animation/product-item.js.
 * Preserves all existing data-attribute/class hooks so no Liquid markup changes were
 * required for this pass. Communicates with assets/quick-view-modal.js via native
 * `quick-view:open` / `quick-view:close` CustomEvents (replaces the shared evx bus).
 */
;(() => {
  'use strict'

  const cartRoutes = (window.theme && window.theme.routes && window.theme.routes.cart) || {}
  const cartAddPath = `${cartRoutes.add || '/cart/add'}.js`

  function addItemById(id, quantity) {
    return fetch(cartAddPath, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity }] }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.status == '422') {
          const error = { code: 422, message: res.description }
          throw error
        }
        return { res }
      })
  }

  function initProductItemAnimations(items) {
    const events = []
    items.forEach((item) => {
      const imageTwo = item.querySelector('.product-item__image--two')

      const enter = () => imageTwo?.classList.add('active')
      const leave = () => imageTwo?.classList.remove('active')

      item.addEventListener('mouseenter', enter)
      item.addEventListener('mouseleave', leave)
      events.push(() => {
        item.removeEventListener('mouseenter', enter)
        item.removeEventListener('mouseleave', leave)
      })
    })

    return {
      destroy() {
        events.forEach((off) => off())
      },
    }
  }

  function initProductItem(container) {
    const items = Array.from(container.querySelectorAll('.product-item'))
    if (!items.length) return { unload() {} }

    items.forEach((item, i) => item.style.setProperty('--z-index-item', items.length - i))

    const animations = initProductItemAnimations(items)
    const quickAddButtons = Array.from(container.querySelectorAll('[data-quick-shop-trigger="quick-add"]'))
    const quickViewButtons = Array.from(container.querySelectorAll('[data-quick-shop-trigger="quick-view"]'))

    const events = []

    function trackQuickAddClick(buttonEl) {
      if (!window.oneskin || !window.oneskin.events) return
      const card = buttonEl.closest('.product-item')
      const productName =
        (card && card.querySelector('.product-item__product-title, .product-item__title'))?.textContent?.trim() ||
        buttonEl.getAttribute('aria-label') ||
        buttonEl.getAttribute('title') ||
        ''
      let itemId = buttonEl.dataset.productId
      if (!itemId && buttonEl.dataset.productUrl) {
        try {
          itemId = new URL(buttonEl.dataset.productUrl, window.location.origin).searchParams.get('variant')
        } catch (err) {
          itemId = undefined
        }
      }
      window.oneskin.events.track('quick_add_click', {
        product_name: productName,
        item_id: itemId ? String(itemId) : undefined,
        source_location: 'plp',
      })
    }

    quickAddButtons.forEach((button) => {
      const handler = (e) => {
        const buttonEl = e.currentTarget
        e.preventDefault()
        e.stopPropagation()

        buttonEl.classList.add('loading')
        const { productId } = buttonEl.dataset
        if (!productId) return

        trackQuickAddClick(buttonEl)
        if (window.oneskin && window.oneskin.events) {
          window.oneskin.events.prepare('add_to_cart', {
            source_location: 'plp',
            product_name:
              buttonEl.closest('.product-item')?.querySelector('.product-item__product-title, .product-item__title')
                ?.textContent?.trim() || '',
            item_id: String(productId),
          })
        }

        addItemById(productId, 1)
          .then(() => {
            buttonEl.classList.remove('loading')
            if (window.NSHybridCart) window.NSHybridCart.open()
          })
          .catch((error) => {
            buttonEl.classList.remove('loading')
            document.dispatchEvent(new CustomEvent('quick-add:error', { detail: { id: productId, error } }))
            console.error('[product-item] quick add failed', error)
          })
      }
      button.addEventListener('click', handler)
      events.push(() => button.removeEventListener('click', handler))
    })

    quickViewButtons.forEach((button) => {
      const handler = (e) => {
        e.preventDefault()
        e.stopPropagation()
        const { productUrl } = e.currentTarget.dataset
        if (!productUrl) return

        trackQuickAddClick(e.currentTarget)
        document.dispatchEvent(
          new CustomEvent('quick-view:open', {
            detail: { productUrl },
          })
        )
      }
      button.addEventListener('click', handler)
      events.push(() => button.removeEventListener('click', handler))
    })

    return {
      unload() {
        animations.destroy()
        events.forEach((off) => off())
      },
    }
  }

  window.OneskinProductItem = initProductItem

  const instances = new WeakMap()

  function autoInit(root = document, { force = false } = {}) {
    // Cards can be grouped under many different section roots (collection grids,
    // search results, recommendation carousels, etc.) — initialize per closest
    // reasonable container so re-init after AJAX refresh only touches new markup.
    const scope = root && root.querySelectorAll ? root : document
    const containers = new Set()

    scope.querySelectorAll('.product-item').forEach((item) => {
      const container =
        item.closest('[data-section-type]') || item.closest('main') || item.parentElement || document.body
      containers.add(container)
    })

    containers.forEach((container) => {
      if (instances.has(container)) {
        if (!force) return
        instances.get(container)?.unload?.()
        instances.delete(container)
      }
      instances.set(container, initProductItem(container))
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit())
  } else {
    autoInit()
  }

  // Re-init whenever new product cards are injected into the page (AJAX pagination,
  // filters, recommendations, quick view sibling swaps, etc).
  document.addEventListener('product-item:reinit', (event) => {
    autoInit(event.detail?.root || document, { force: true })
  })

  document.addEventListener('collection:updated', () => {
    autoInit(document, { force: true })
  })

  document.addEventListener('shopify:section:load', (event) => {
    autoInit(event.target, { force: true })
  })
})()
